import {
  Box,
  Button,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import appRoutes from "app/config/routes";
import React, { useMemo, useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import InstallMobileIcon from "@mui/icons-material/InstallMobile";
import TransportStudentMapFilter from "app/components/common/Dialogs/transport/TransportStudentMapFilter";
import InfiniteScrollTable from "app/components/Tables/InfiniteScrollTable";
import {
  deleteTransportStudentMap,
  getStudentTransportMapDetail,
} from "app/services/schoolService/transport.service";
import { FormatFullName } from "app/utils/helper";
import TableActionButtons from "app/components/common/tableComponent/TableActionButtons";
import { useSelector } from "react-redux";
import { selectActiveSession } from "app/modules/schools/selectors";
import AddTransportStudentMap from "app/components/common/Dialogs/transport/AddTransportStudentMap";
import DeleteDialog from "app/components/ConfirmationDialog/DeleteDialog";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import ViewTransportDialog from "app/components/common/Dialogs/transport/ViewTransportDialog";
import ExportButtonGroup from "app/components/common/ExportButtonGroup";
import ExportDialog from "app/components/common/exportDialog";
import CommonPdfReport from "app/components/pdfReport/CommonPdfReport";
import dayjs from "dayjs";
import { useReactToPrint } from "react-to-print";
import TransportCardModal from "./components/TransportCardModal";
import { Add } from "@mui/icons-material";

const TransportStudentMap = () => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const selectedSession = useSelector(selectActiveSession);
  const [openCheckBox, setOpenCheckBox] = useState(false);
  const [allCheck, setAllCheck] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [filteredData, setFilteredData] = useState([]);
  const [showTransportCardModal, setShowTransportCardModal] = useState(false);
  const [template, setTemplate] = useState(1);
  const [color, setColor] = useState("#e6ffe9");
  const printRef = useRef(null);
  const [state, setState] = React.useState({
    tableData: [],
    totalTableData: 0,
    viewDialogData: {},
    editDialogData: {},
    deleteDialogData: {},
    viewDialog: false,
    editDialog: false,
    deleteDialog: false,
    addNewTransportStudentMap: false,
    pdfExportDialog: false,
  });

  // Print functionality
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: "Transport Card",
    pageStyle: `
      @page {
        size: A4;
        margin: 20mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
        }
      }
    `,
  });

  const columns = useMemo(() => {
    return [
      {
        header: "#",
        accessorKey: "#",
        Cell: ({ cell }) => (
          <Typography>{parseInt(cell.row.id) + 1}</Typography>
        ),
        maxSize: isMobile ? 20 : 60,
      },
      {
        header: "Identity",
        accessorKey: "studentDocId.admissionNumber",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          return (
            <Box>
              <div title="SR Number">
                <Typography>
                  SR No : {original?.studentDocId?.srNumber ?? "--"}
                </Typography>
              </div>
              <div title="Admission Number">
                <Typography sx={{ color: "#2789FD" }}>
                  Adm No : {original?.studentDocId?.admissionNumber ?? "--"}
                </Typography>
              </div>
              <div title="Reg. Number">
                <Typography sx={{ color: "#2789FD" }}>
                  Reg No : {original?.studentDocId?.registrationNumber ?? "--"}
                </Typography>
              </div>
              <div title="PEN Number">
                <Typography>
                  PEN No : {original?.studentDocId?.penNumber_center ?? "--"}
                </Typography>
              </div>
            </Box>
          );
        },
        maxSize: isMobile ? 100 : 150,
      },
      {
        header: "Student Name",
        accessorKey: "studentDocId.firstName",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          return (
            <>
              <Stack direction="row" alignItems="center" gap={0.6}>
                <Link
                  to={`/${appRoutes.student_profile}/${original?.studentDocId?._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Typography>
                    {FormatFullName(
                      original?.studentDocId?.firstName,
                      original?.studentDocId?.middleName,
                      original?.studentDocId?.lastName,
                    )}
                  </Typography>
                </Link>
                {original?.studentDocId?.appNotificationToken && (
                  <Tooltip title="This Student have downloaded Ekalsutra App">
                    <InstallMobileIcon />
                  </Tooltip>
                )}
              </Stack>
            </>
          );
        },
        maxSize: isMobile ? 120 : 180,
      },
      {
        header: "Class - Section",
        accessorKey: "classInfo.className",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          return (
            <Typography>
              {original?.classInfo?.className ?? "--"} -{" "}
              {original?.classInfo?.section ?? "--"}
            </Typography>
          );
        },
        maxSize: isMobile ? 100 : 170,
      },
      {
        header: "Using",
        accessorKey: "using",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          return (
            <Typography
              sx={{
                color: original?.using === "No" ? "#FF0000" : "green",
              }}
            >
              {original?.using ?? "--"}
            </Typography>
          );
        },
        maxSize: isMobile ? 80 : 120,
      },
      {
        header: "Father Name",
        accessorKey: "studentDocId.fatherInfo.firstName",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          return (
            <>
              <Stack direction="row" alignItems="center" gap={0.6}>
                <Typography>
                  {FormatFullName(
                    original?.studentDocId?.fatherInfo?.firstName,
                    original?.studentDocId?.fatherInfo?.middleName,
                    original?.studentDocId?.fatherInfo?.lastName,
                  )}
                </Typography>
              </Stack>
            </>
          );
        },
        maxSize: isMobile ? 120 : 180,
      },
      {
        header: "Start Date",
        accessorKey: "startDate",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          return (
            <Typography>
              {original?.startDate
                ? dayjs(original?.startDate).format("DD MMM YYYY")
                : "--"}
            </Typography>
          );
        },
        maxSize: isMobile ? 100 : 150,
      },
      {
        header: "Vehicle Number",
        accessorKey: "vehicleNumber",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          return (
            <Typography>
              {original?.vehicleNumber
                ? `${original?.vehicleNumber ?? ""}${
                    original?.vehicleNumber && original?.vehicleNickName
                      ? " - "
                      : ""
                  }${original?.vehicleNickName ?? ""}`
                : "--"}
            </Typography>
          );
        },
        maxSize: isMobile ? 130 : 180,
      },
      {
        header: "Route",
        accessorKey: "routeNumber",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          return <Typography>{original?.routeNumber}</Typography>;
        },
        maxSize: isMobile ? 80 : 150,
      },
      {
        header: "Range Code",
        accessorKey: "rangeCode",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          return <Typography>{original?.rangeCode ?? "--"}</Typography>;
        },
        maxSize: isMobile ? 100 : 150,
      },
      {
        header: "Stop Name",
        accessorKey: "to",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          return <Typography>{original?.to || "--"}</Typography>;
        },
        maxSize: isMobile ? 130 : 180,
      },
      {
        header: "Fee Amount",
        accessorKey: "feeAmount",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          return <Typography>{original?.feeAmount ?? "--"}</Typography>;
        },
        maxSize: isMobile ? 100 : 150,
      },
      {
        header: "Mobile No.",
        accessorKey: "studentDocId.mobileNumber",
        Cell: ({
          cell: {
            row: { original },
          },
        }) => {
          return (
            <Typography>
              {original?.studentDocId?.mobileNumber ?? "--"}
            </Typography>
          );
        },
        maxSize: isMobile ? 120 : 180,
      },
      {
        header: "Action",
        Header: () => {
          return (
            <Stack direction="row" alignItems="center" gap={0.8}>
              <input
                type="checkbox"
                id="checkdiv"
                checked={allCheck}
                style={{ cursor: "pointer", width: "20px", height: "20px" }}
                onChange={(e) => setAllCheck((prev) => !prev)}
              />
              <Typography>Actions</Typography>
            </Stack>
          );
        },

        Cell: ({ cell, row }) => {
          const { original } = row;
          const rowId = original._id || row.id;
          return (
            <Stack direction="row" alignItems="center" gap={1}>
              <input
                type="checkbox"
                id="checkdiv"
                checked={selectedRows.has(rowId)}
                style={{ cursor: "pointer", width: "20px", height: "20px" }}
                onChange={(e) => {
                  setSelectedRows((prev) => {
                    const newSet = new Set(prev);
                    if (newSet.has(rowId)) {
                      newSet.delete(rowId);
                    } else {
                      newSet.add(rowId);
                    }
                    return newSet;
                  });
                }}
              />
              <TableActionButtons
                handleView={() => {
                  setState((prev) => ({
                    ...prev,
                    viewDialog: true,
                    viewDialogData: {
                      ...original,
                      ...(original?.transportInfo || {}),
                    },
                  }));
                }}
                handleEdit={() => {
                  setState((prev) => ({
                    ...prev,
                    editDialog: true,
                    editDialogData: original,
                  }));
                }}
                handleDelete={() => {
                  setState((prev) => ({
                    ...prev,
                    deleteDialog: true,
                    deleteDialogData: original,
                  }));
                }}
                showDelete={true}
                showView={true}
                showEdit={true}
              />
            </Stack>
          );
        },

        maxSize: isMobile ? 140 : 200,
      },
    ];
  }, [allCheck, selectedRows, isMobile]);

  // allCheck functionality
  useEffect(() => {
    if (allCheck) {
      // Select all rows
      const allRowIds = new Set(
        state.tableData.map((item) => item._id || item.id),
      );
      setSelectedRows(allRowIds);
    } else {
      // Deselect all rows
      setSelectedRows(new Set());
    }
  }, [allCheck, state.tableData]);

  // Function to get filtered data for printing
  const getSelectedData = () => {
    return state.tableData.filter((item) => {
      const rowId = item._id || item.id;
      return selectedRows.has(rowId);
    });
  };

  // Function to open transport card modal
  const handlePrintTransportCard = () => {
    const selected = getSelectedData();
    if (selected.length > 0) {
      setFilteredData(selected);
      setShowTransportCardModal(true);
    } else {
      toast.warning("Please select at least one student");
    }
  };

  // Function to close transport card modal
  const closeTransportCardModal = () => {
    setShowTransportCardModal(false);
  };

  const [enabled, setEnabled] = useState(false);
  const [mutateData, setMutateData] = useState({ using: "Yes" });

  // Reset selections when table data changes (like when filters are applied)
  useEffect(() => {
    setSelectedRows(new Set());
    setAllCheck(false);
  }, [enabled]); // This will trigger when data is refreshed

  const handleMutateData = (data) => {
    setMutateData((prev) => ({
      ...data,
    }));
    setEnabled((prev) => !prev);
  };

  const {
    mutate: deleteTransportStudentMapMutate,
    isLoading: deleteTransportStudentMapLoading,
  } = useMutation("deleteTransportStudentMap", deleteTransportStudentMap, {
    onSuccess: ({ data }) => {
      toast.success(data?.message);
      setState((prev) => ({
        ...prev,
        deleteDialog: false,
      }));

      setEnabled((prev) => !prev);
    },
    onError: ({ response: { data } }) => {
      toast.error(data?.message);
    },
  });

  const header = [
    "#",
    "Roll No",
    "SR No",
    "Adm. No",
    "Reg. No",
    "PEN No",
    "Student Name",
    "Class - Section",
    "Father Name",
    "Mobile Number",
    "Trans. Using",
    "Vehicle Number",
    "Route",
    "Range Code",
    "Stop Name",
    "Fee Amount",
  ];

  // Prepare export data (following reference pattern)
  let exportArray =
    state?.tableData?.map((item, index) => {
      return {
        "#": index < 9 ? `0${index + 1}` : index + 1,
        "Roll No": item?.studentDocId?.rollNumber ?? "--",
        "SR No": item?.studentDocId?.srNumber ?? "--",
        "Adm. No": item?.studentDocId?.admissionNumber ?? "--",
        "Reg. No": item?.studentDocId?.registrationNumber ?? "--",
        "PEN No": item?.studentDocId?.penNumber_center ?? "--",
        "Student Name": FormatFullName(
          item?.studentDocId?.firstName,
          item?.studentDocId?.middleName,
          item?.studentDocId?.lastName,
        ),
        "Class - Section": `${item?.classInfo?.className ?? "--"} - ${
          item?.classInfo?.section ?? "--"
        }`,
        "Father Name": FormatFullName(
          item?.studentDocId?.fatherInfo?.firstName,
          item?.studentDocId?.fatherInfo?.middleName,
          item?.studentDocId?.fatherInfo?.lastName,
        ),
        "Mobile Number": item?.studentDocId?.mobileNumber ?? "--",
        "Trans. Using": item?.using ?? "--",
        "Vehicle Number": item?.vehicleNumber
          ? `${item?.vehicleNumber ?? ""}${
              item?.vehicleNumber && item?.vehicleNickName ? " - " : ""
            }${item?.vehicleNickName ?? ""}`
          : "--",
        Route: item?.routeNumber ?? "--",
        "Range Code": item?.rangeCode ?? "--",
        "Stop Name": item?.to ?? "--",
        "Fee Amount": item?.feeAmount ?? "--",
      };
    }) || [];

  return (
    <>
      <Helmet>
        <title> Transport Student | Transportation</title>
      </Helmet>

      <DeleteDialog
        open={state.deleteDialog}
        handleClose={() => {
          setState((prev) => ({
            ...prev,
            deleteDialog: false,
          }));
        }}
        handleDelete={() => {
          deleteTransportStudentMapMutate({
            feeStructureDocId: state.deleteDialogData?.transportFeeDocId,
            studentDocId: state.deleteDialogData?.studentDocId?._id,
            session: selectedSession,
          });
        }}
        loading={deleteTransportStudentMapLoading}
        title={`${FormatFullName(
          state.deleteDialogData?.studentDocId?.firstName,
          state.deleteDialogData?.studentDocId?.middleName,
          state.deleteDialogData?.studentDocId?.lastName,
        )}`}
      />

      <AddTransportStudentMap
        refetch={() => {
          setEnabled((prev) => !prev);
        }}
        dialog={state.addNewTransportStudentMap || state.editDialog}
        onClose={() => {
          setState((prev) => ({
            ...prev,
            addNewTransportStudentMap: false,
            editDialog: false,
            editDialogData: {},
          }));
        }}
        editData={state.editDialogData}
        showStudentSearch={state?.editDialog ? false : true}
      />

      <ViewTransportDialog
        open={state.viewDialog}
        handleClose={() => {
          setState((prev) => ({
            ...prev,
            viewDialog: false,
          }));
        }}
        viewDialogData={state.viewDialogData}
        handleEditDialogOpen={() => {
          setState((prev) => ({
            ...prev,
            viewDialog: false,
            editDialog: true,
            editDialogData: state.viewDialogData,
          }));
        }}
      />

      <CommonPdfReport
        open={state.pdfExportDialog}
        onClose={() => {
          setState((prev) => ({ ...prev, pdfExportDialog: false }));
        }}
        header={header}
        exportArray={exportArray}
        exportTitle="Transport_Student_Map.pdf"
        filterData={mutateData}
        showFiter={true}
      />

      <ExportDialog
        open={openCheckBox}
        handleClose={() => setOpenCheckBox(false)}
        header={header}
        exportArray={exportArray}
        fileName="Transport_Student_Map"
      />

      {/* Transport Card Print Modal */}
      <TransportCardModal
        open={showTransportCardModal}
        onClose={closeTransportCardModal}
        filteredData={filteredData}
        printRef={printRef}
        handlePrint={handlePrint}
        selectedSession={selectedSession}
        template={template}
        setTemplate={setTemplate}
        color={color}
        setColor={setColor}
      />

      <Box>
        <Stack
          direction="row"
          alignItems={isMobile ? "start" : "center"}
          justifyContent="space-between"
        >
          <TransportStudentMapFilter mutate={handleMutateData} />

          <Stack
            direction={isMobile ? "column" : "row"}
            alignItems="center"
            gap={2}
          >
            <Typography variant="h6">
              Total Students: {state.totalTableData}
            </Typography>
            <Stack direction="row" gap={1} alignItems="center">
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => {
                  setState((prev) => ({
                    ...prev,
                    addNewTransportStudentMap: true,
                  }));
                }}
                startIcon={<Add />}
              >
                Add New Student
              </Button>

              {getSelectedData().length > 0 && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handlePrintTransportCard}
                  size="large"
                >
                  Print
                </Button>
              )}
            </Stack>
            <ExportButtonGroup
              handleClickExcel={() => setOpenCheckBox(true)}
              handleClickPdf={() => {
                setState((prev) => ({
                  ...prev,
                  pdfExportDialog: true,
                }));
              }}
              showPdf={true}
              showExcel={true}
            />
          </Stack>
        </Stack>
        <Box sx={{ mt: "1rem", marginBottom: isMobile ? 12 : 0 }}>
          {console.log("state: ", state)}
          <InfiniteScrollTable
            columns={columns}
            maxHeight={isMobile ? "60vh" : "65vh"}
            apiUrl={getStudentTransportMapDetail}
            apiParams={
              {
                session: selectedSession,
                ...mutateData,
              } || {}
            }
            initialState={{
              columnPinning: isMobile
                ? {
                    right: ["Action"],
                  }
                : {
                    left: ["#"],
                    right: ["Action"],
                  },
            }}
            setState={setState}
            enabled={enabled}
            setEnabled={setEnabled}
          />
        </Box>
      </Box>
    </>
  );
};

export default TransportStudentMap;
